import type { Styles } from '@adornis/ass/style';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element';
import { LocalStorageBehaviorController } from '@adornis/chemistry/controllers/local-storage-behavior-controller';
import { css } from '@adornis/chemistry/directives/css';
import '@adornis/chemistry/elements/components/x-flex';
import '@adornis/chemistry/elements/components/x-grid';
import '@adornis/chemistry/elements/components/x-icon';
import { XDialog } from '@adornis/dialog/x-dialog';
import '@adornis/forms/x-checkbox';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import {
  Question1,
  Question2,
  Question3,
  Question4,
  Question5,
  Question6,
  Question8,
  Question9,
  type AICheckerAnswer,
  type BaseQuestion,
} from '../../db/questions';
import { Result } from '../../db/result';
import '../components/fps-button';
import '../components/fps-checkbox';
import '../components/fps-fonts';
import '../components/fps-progress-bar';
import { FpsProgressBar } from '../components/fps-progress-bar';

/**
 * @element fps-checker
 */
@customElement('fps-checker')
export class FpsChecker extends ChemistryLitElement {
  override styles(): Styles[] {
    return [
      ...super.styles(),
      {
        ':host': {
          display: 'block',
          minHeight: '800px',
          paddingBottom: '300px',
        },
        '#container': {
          width: '100%',
          minHeight: '500px',
          maxWidth: '600px',
          margin: '0 auto',
        },
        ul: {
          marginBlockStart: '0',
          marginBlockEnd: '0',
        },
        li: {
          padding: '10px 0',
        },
      },
    ];
  }

  override connectedCallback(): void {
    super.connectedCallback();

    this._questions.push(new Question1());
    this._questions.push(new Question2());
    this._questions.push(new Question3());
    this._questions.push(new Question4());
    this._questions.push(new Question5());
    this._questions.push(new Question6());
    this._questions.push(new Question8());
    this._questions.push(new Question9());

    // read answers from local storage
    const val = this._localStorage.value;
    if (val) {
      const map = new Map(Object.entries(val));
      this._answers = map;
    }
  }

  @state() private readonly _questions: BaseQuestion[] = [];
  @state() private _answers = new Map<string, AICheckerAnswer[]>();
  @state() private readonly _localStorage = new LocalStorageBehaviorController<Record<string, AICheckerAnswer[]>>(
    this,
    'fps-checker',
    {},
  );

  @state() private _currentQuestionStep = 0;
  @state() private _finished = false;

  private readonly _progressBarRef = createRef<FpsProgressBar>();

  override render() {
    return html`
      <x-flex id="container" space="lg">
        <fps-progress-bar
          ${ref(this._progressBarRef)}
          .value=${!this._finished ? this._currentQuestionStep / (this._questions.length - 1) : 1}
        ></fps-progress-bar>
        ${this._finished ? this._resultScreen() : this._questionDisplay()}
      </x-flex>
      ${this._finished ? html` <slot> </slot> ` : nothing}
    `;
  }

  private _questionDisplay() {
    if (this._currentQuestionStep >= this._questions.length) return this._finalizeScreen();
    const question = this._questions[this._currentQuestionStep];
    if (!question) return html` <fps-text> No questions found. </fps-text> `;

    let answers = this._answers.get(this._currentQuestionStep.toString());
    if (!answers) {
      answers = [];
      this._answers.set(this._currentQuestionStep.toString(), answers);
      const obj = Object.fromEntries(this._answers);
      this._localStorage.next(obj);
    }

    switch (question.type) {
      case 'single':
        return this._displayQuestionSingle(question, answers);
      case 'multiple':
        return this._displayQuestionMultiple(question, answers);
      default:
        return html` <fps-text> Unsupported question type. </fps-text> `;
    }
  }

  private _displayQuestionSingle(question: BaseQuestion, answers: AICheckerAnswer[]) {
    return html`
      <x-flex space="lg">
        <x-flex space="md"> ${this._questionHeader(question, answers)} ${this._explanationDisplay(question)} </x-flex>
        <x-grid columns="max-content" space="md">
          ${repeat(question.choices, choice => html` ${this._choiceButton(question, choice, answers)} `)}
        </x-grid>
      </x-flex>
    `;
  }

  private _displayQuestionMultiple(question: BaseQuestion, answers: AICheckerAnswer[]) {
    return html`
      <x-flex space="lg">
        ${this._questionHeader(question, answers)} ${this._explanationDisplay(question)}
        <x-flex ${css({ gap: '22px' })}>
          ${repeat(question.choices, choice => html` ${this._choiceButton(question, choice, answers)} `)}
        </x-flex>
        <x-flex crossaxis-start>
          ${answers.length > 0
            ? html`
                <fps-button
                  @click=${() => {
                    const allAnswers = Array.from(this._answers.values()).flat();
                    if (!question.shouldContinue(allAnswers)) {
                      this._finished = true;
                    }
                    this._goForward();
                  }}
                >
                  WEITER
                </fps-button>
              `
            : nothing}
        </x-flex>
      </x-flex>
    `;
  }

  private _questionHeader(question: BaseQuestion, answers: AICheckerAnswer[]) {
    return html`
      <x-flex space="lg">
        ${this._navButtons(question, answers)}
        <fps-text bold> ${question.question} </fps-text>
      </x-flex>
    `;
  }

  private _navButtons(question?: BaseQuestion, answers: AICheckerAnswer[] = []) {
    const nextAnswered =
      this._currentQuestionStep < this._questions.length - 1
        ? this._answers.has((this._currentQuestionStep + 1).toString())
        : true;
    const allAnswers = Array.from(this._answers.values()).flat();
    return html`
      <x-flex horizontal end space="sm">
        ${this._currentQuestionStep === 0 || this._finished
          ? html`
              <fps-button
                mode="icon"
                icon="refresh"
                @click=${async () => {
                  if (
                    !(await XDialog.confirm(
                      'Soll der KI-Checker seine aktuellen Daten löschen und von vorne beginnen?',
                      undefined,
                      { targetParent: document.body },
                    ))
                  )
                    return;
                  this._localStorage.next({});
                  this._answers = new Map();
                  this._finished = false;
                  this._currentQuestionStep = 0;
                  this.requestUpdate();
                }}
              ></fps-button>
            `
          : nothing}
        <fps-button
          mode="icon"
          icon="arrow_back"
          ?disabled=${this._currentQuestionStep < 1}
          @click=${() => this._goBack()}
        ></fps-button>
        <fps-button
          mode="icon"
          icon="arrow_forward"
          ?disabled=${!nextAnswered ||
          !question?.shouldContinue(allAnswers) ||
          (this._currentQuestionStep === this._questions.length - 1 && !answers.length)}
          @click=${() => this._goForward()}
        ></fps-button>
      </x-flex>
    `;
  }

  private _explanationDisplay(question: BaseQuestion) {
    if (question.getExplanation() === null) return nothing;
    return html`
        <fps-text> ${question.getExplanation()} </fps-text></fps-text>
    `;
  }

  private _choiceButton(question: BaseQuestion, choice: AICheckerAnswer, answers: AICheckerAnswer[]) {
    if (question.type === 'multiple')
      return html`
        <fps-checkbox
          .label=${question.getChoiceText(choice)}
          .value=${answers.includes(choice)}
          @click=${() => {
            const lastChoice = question.choices[question.choices.length - 1];
            if (answers.includes(choice)) {
              answers.splice(answers.indexOf(choice), 1);
            } else {
              if (lastChoice === choice) {
                answers = [choice];
              } else {
                const lastChoiceIdx = lastChoice ? answers.indexOf(lastChoice) : -1;
                if (lastChoiceIdx >= 0) {
                  answers.splice(lastChoiceIdx, 1);
                }
                answers.push(choice);
              }
            }

            this._answers.set(this._currentQuestionStep.toString(), answers);
            const obj = Object.fromEntries(this._answers);
            this._localStorage.next(obj);
            this.requestUpdate();
          }}
        ></fps-checkbox>
      `;
    const selected = answers.includes(choice);
    return html`
      <fps-button
        ?selected=${selected}
        ${css({ minWidth: '200px' })}
        @click=${() => {
          if (question.type === 'single') {
            answers = [];
            answers.push(choice);
          } else if (answers.includes(choice)) {
            answers.splice(answers.indexOf(choice), 1);
          } else {
            answers.push(choice);
          }

          this._answers.set(this._currentQuestionStep.toString(), answers);
          const obj = Object.fromEntries(this._answers);
          this._localStorage.next(obj);

          const allAnswers = Array.from(this._answers.values()).flat();
          if (!question.shouldContinue(allAnswers)) {
            this._finished = true;
          }
          this._goForward();
        }}
      >
        ${question.getChoiceText(choice).toUpperCase()}
      </fps-button>
    `;
  }

  private _finalizeScreen() {
    return html`
      <x-flex space="lg">
        <x-flex space="lg">
          ${this._navButtons()}
          <fps-text> Möchten Sie hiermit Ihre Antworten abschicken? </fps-text>
        </x-flex>
        <x-flex space="md">
          <x-flex horizontal space="lg">
            <fps-button
              .icon=${'send'}
              @click=${() => {
                this._finished = true;
              }}
            >
              ABSENDEN!
            </fps-button>
          </x-flex>
        </x-flex>
      </x-flex>
    `;
  }

  private _resultScreen() {
    const answers = Array.from(this._answers.values()).flat();
    const result = new Result();
    return html` <x-flex space="lg"> ${this._navButtons()} ${result.generateResult(answers)} </x-flex> `;
  }

  private _goBack() {
    const allAnswers = Array.from(this._answers.values()).flat();
    if (this._currentQuestionStep > 0) {
      let newPreviousQuestion;
      do {
        this._currentQuestionStep--;
        this._finished = false;
      } while (
        this._currentQuestionStep >= 0 &&
        (newPreviousQuestion = this._questions[this._currentQuestionStep]) &&
        newPreviousQuestion.isSkipped(allAnswers)
      );
      this._scrollIntoView();
    }
  }

  private _goForward() {
    const allAnswers = Array.from(this._answers.values()).flat();
    let newQuestion;
    do {
      this._currentQuestionStep++;
    } while (
      (!(newQuestion = this._questions[this._currentQuestionStep]) || newQuestion.isSkipped(allAnswers)) &&
      this._currentQuestionStep < this._questions.length
    );
    this._scrollIntoView();
  }

  private _scrollIntoView() {
    requestAnimationFrame(() => this._progressBarRef.value?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }));
  }
}
